import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const LABELS = {
    missing_signature: "Missing signature",
    bad_signature: "Invalid signature (check the shared secret)",
    too_large: "Request too large",
    invalid_json: "Body is not valid JSON",
    invalid_payload: "Invalid or missing fields",
    event_id_mismatch: "Event ID does not match submission ID",
    invalid_phone: "Invalid phone number",
    payload_conflict: "Submission ID reused with different data",
    not_configured: "Webhook not configured (website will retry)",
    storage_not_ready: "Database indexes missing (website will retry)",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/Los_Angeles" }) + " PT" : "—");

export const WebsiteRejections = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const load = useCallback(async () => {
        try { setData((await api.get("/website-reviews/rejections")).data); setError(null); }
        catch (e) { setError(formatApiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);
    const suppressed = (data?.days || []).reduce((n, d) => n + (d.suppressed || 0), 0);
    return (
        <section className="space-y-3" data-testid="website-rejections">
            <div>
                <h2 className="text-base md:text-lg font-semibold">Rejected website requests</h2>
                <p className="text-sm text-muted-foreground max-w-2xl">
                    Requests CaseFlow refused. Nothing was created or sent. Repeats within an hour are merged.
                    Kept {data?.retention_days ?? 30} days. Failures on the website itself never reach CaseFlow and stay in the website's own error log.
                </p>
            </div>
            {error && <div className="text-sm text-red-700" data-testid="website-rejections-error">{error}</div>}
            {!data && !error && <Loader2 className="h-5 w-5 animate-spin" />}
            {suppressed > 0 && (
                <div className="text-sm rounded border border-amber-300 bg-amber-50 p-2" data-testid="website-rejections-suppressed">
                    {suppressed.toLocaleString()} more rejected requests weren't listed after the daily limit of {data.daily_cap.toLocaleString()} new rows.
                </div>
            )}
            {data && data.items.length === 0 && <div className="text-sm text-muted-foreground" data-testid="website-rejections-empty">No rejected requests in the last {data.retention_days} days.</div>}
            {data && data.items.length > 0 && (
                <div className="overflow-x-auto rounded-xl border bg-white">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50/70 text-left text-xs uppercase tracking-wider text-muted-foreground">
                            <tr><th className="p-3">Problem</th><th className="p-3">HTTP</th><th className="p-3">Count</th><th className="p-3">First seen</th><th className="p-3">Last seen</th><th className="p-3">Source</th><th className="p-3">Submission IDs</th></tr>
                        </thead>
                        <tbody>
                            {data.items.map((r) => (
                                <tr key={r.id} className="border-t align-top" data-testid={`rejection-row-${r.id}`}>
                                    <td className="p-3">
                                        <div data-testid={`rejection-category-${r.id}`}>{LABELS[r.category] || r.category}</div>
                                        {r.detail && <div className="text-xs text-muted-foreground break-words">{r.detail}</div>}
                                    </td>
                                    <td className="p-3" data-testid={`rejection-status-${r.id}`}>{r.http_status}</td>
                                    <td className="p-3" data-testid={`rejection-count-${r.id}`}>{r.count}</td>
                                    <td className="p-3 whitespace-nowrap">{fmt(r.first_seen_at)}</td>
                                    <td className="p-3 whitespace-nowrap">{fmt(r.last_seen_at)}</td>
                                    <td className="p-3 text-xs">{r.ip_hint || "—"}</td>
                                    <td className="p-3 text-xs font-mono break-all" data-testid={`rejection-sids-${r.id}`}>{(r.submission_ids || []).join(", ") || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};
